// `decryptE2e` — recover the plaintext from an envelope, given the
// recipient's identity private key and account-id-hash.
//
// Inverse of `encryptE2e` (see encrypt.ts for the full pipeline). The
// recipient half:
//
//   1. Locate the `recipients[]` slot whose `accountIdHash` matches
//      this caller. If none matches, fail with `not_recipient` —
//      the caller is not in the envelope's intended audience.
//   2. ECDH(myPrivateKey, env.senderEphPub)  -> 32-byte shared secret
//   3. HKDF-SHA256 with the same salt/info as the sender to recover
//      the per-recipient wrap key.
//   4. AES-256-GCM-decrypt `wrappedKey` under the wrap key, with no
//      AAD, IV from the slot. Result: the per-message key `MK_msg`.
//   5. AES-256-GCM-decrypt `ct` under `MK_msg` with the envelope's
//      content IV and the AAD reconstructed from `(channelId, messageId)`.
//      The content AAD MUST equal `env.aad` byte-for-byte (we check
//      this explicitly so a tampered `aad` field surfaces as a clean
//      `decrypt_failure` rather than a confusing tag mismatch).
//
// All AES-GCM calls leverage Web Crypto's tag verification — a wrong
// recipient key, a swapped envelope, or a tampered IV/ciphertext all
// surface as `subtle.decrypt` rejecting the call, which we re-throw
// as `E2eError("decrypt_failure")`. The caller never sees which step
// failed; the design's "AES-GCM tag mismatch on E2E decrypt is a hard
// error surfaced as 'this message could not be decrypted'" rule is
// implemented here.
//
// _Requirements: 8.2, 11.5, 15.3, 15.4_

import {
  buildE2eAad,
  type E2eEnvelope,
  E2eError,
} from "./envelope";
import {
  ECDH_SHARED_SECRET_BYTES,
  GCM_TAG_BYTES,
  MK_BYTES,
  ctEqual,
  deriveWrapKey,
  getSubtle,
  wipe,
} from "./internals";

// ---------------------------------------------------------------------------
// Public input type
// ---------------------------------------------------------------------------

/** Inputs to {@link decryptE2e}. */
export interface DecryptE2eInput {
  /** Decoded envelope (see `parseE2eEnvelope`). */
  envelope: E2eEnvelope;
  /**
   * Recipient's ECDSA P-256 identity private key, exactly as
   * `deriveIdentityKey` returns. The same private scalar that backs
   * the recipient's `IK_pub`; we re-import it for ECDH inside this
   * function (Web Crypto requires algorithm-specific keys, and
   * ECDSA-tagged keys cannot be used directly for `deriveBits`).
   *
   * The recipient MAY pass a key that is already an ECDH key — in
   * that case `recipientPrivateKeyAlg` SHOULD be `"ECDH"`. Default is
   * `"ECDSA"` because that matches the identity-key shape `Key_Store`
   * persists.
   */
  recipientPrivateKey: CryptoKey;
  /**
   * The recipient's identity public key in 65-byte uncompressed SEC1
   * form. Required because Web Crypto cannot re-export a
   * non-extractable identity private key, but we need the keypair as
   * an ECDH key to compute the shared secret. We import a fresh
   * ECDH keypair from `(scalar, publicKeyRaw)`-style JWK if the
   * caller hands us one (see `recipientPrivateKeyJwk` below); when
   * the caller hands us a `CryptoKey` directly, the public key isn't
   * actually used here and may be omitted.
   */
  recipientPublicKeyRaw?: Uint8Array;
  /**
   * The recipient's `account_id_hash` (SHA-256 of `publicKeyRaw`).
   * Used to look up the recipient's slot in `envelope.recipients[]`.
   */
  recipientAccountIdHash: Uint8Array;
  /**
   * Optional: an extractable JWK form of the recipient's private key,
   * used to re-import as an ECDH key when the supplied
   * `recipientPrivateKey` is an ECDSA key. If omitted, the supplied
   * `recipientPrivateKey` is used directly under
   * `{ name: "ECDH", namedCurve: "P-256" }`.
   *
   * In the v1 web client the standard flow is: the Key_Store hands
   * back an ECDH-tagged private key freshly imported from JWK at
   * decrypt time. See `apps/web` task 18.5 for the integration.
   */
  recipientPrivateKeyJwk?: JsonWebKey;
  /**
   * Channel ID expected to be bound into the envelope's AAD. The
   * function recomputes the canonical AAD from `(channelId,
   * messageId)` and rejects the envelope if it does not match
   * `envelope.aad`.
   */
  channelId: string;
  /** Message ID expected to be bound into the envelope's AAD. */
  messageId: string;
}

// ---------------------------------------------------------------------------
// Public function
// ---------------------------------------------------------------------------

/**
 * Decrypt an E2E envelope addressed to the calling recipient. Returns
 * the plaintext bytes on success.
 *
 * @throws {@link E2eError} with `kind = "not_recipient"` if the
 *   caller's `accountIdHash` is not in the envelope's recipient list.
 * @throws {@link E2eError} with `kind = "decrypt_failure"` if any
 *   AES-GCM tag mismatch occurs (wrong key, tampered envelope, AAD
 *   mismatch). Per design, the caller surfaces this as a generic
 *   "this message could not be decrypted" — no further detail is
 *   leaked from this function.
 */
export async function decryptE2e(input: DecryptE2eInput): Promise<Uint8Array> {
  const { envelope, recipientAccountIdHash, channelId, messageId } = input;

  if (recipientAccountIdHash.length !== 32) {
    throw new E2eError(
      "bad_field_length",
      `recipientAccountIdHash must be 32 bytes, got ${recipientAccountIdHash.length}`,
    );
  }

  // Step 0: AAD must match what we'd build for this (channel, message).
  // A passing AES-GCM tag check would already cover this — we make
  // the comparison explicit so a mis-routed envelope surfaces with a
  // typed error before we burn an ECDH on it.
  const expectedAad = buildE2eAad(channelId, messageId);
  if (!ctEqual(envelope.aad, expectedAad)) {
    throw new E2eError(
      "decrypt_failure",
      "envelope AAD does not match (channelId, messageId)",
    );
  }

  // Step 1: locate the recipient slot.
  let slotIndex = -1;
  for (let i = 0; i < envelope.recipients.length; i++) {
    if (ctEqual(envelope.recipients[i]!.accountIdHash, recipientAccountIdHash)) {
      slotIndex = i;
      break;
    }
  }
  if (slotIndex === -1) {
    throw new E2eError(
      "not_recipient",
      "the caller is not in the envelope's recipient list",
    );
  }
  const slot = envelope.recipients[slotIndex]!;

  const subtle = getSubtle();

  // Step 2: import the recipient private key as an ECDH key. Identity
  // keys land here as ECDSA — re-import via JWK when one is provided.
  let ecdhPrivate: CryptoKey;
  if (input.recipientPrivateKeyJwk) {
    try {
      ecdhPrivate = await subtle.importKey(
        "jwk",
        // Strip key-usage and alg fields that would carry the ECDSA
        // tag along — we want a fresh ECDH-tagged key.
        stripJwkAlg(input.recipientPrivateKeyJwk),
        { name: "ECDH", namedCurve: "P-256" },
        false,
        ["deriveBits"],
      );
    } catch (cause) {
      throw new E2eError(
        "bad_shape",
        "recipientPrivateKeyJwk could not be imported as an ECDH P-256 key",
        cause,
      );
    }
  } else {
    ecdhPrivate = input.recipientPrivateKey;
  }

  // Import the sender's ephemeral public key as an ECDH key. This
  // also runs Web Crypto's on-curve check.
  let senderEphPub: CryptoKey;
  try {
    senderEphPub = await subtle.importKey(
      "raw",
      envelope.senderEphPub.slice(),
      { name: "ECDH", namedCurve: "P-256" },
      false,
      [],
    );
  } catch (cause) {
    // A senderEphPub that is not on-curve is a tampered/bogus
    // envelope, not a programmer error — fold into the generic
    // decrypt-failure path so callers surface the same toast.
    throw new E2eError(
      "decrypt_failure",
      "envelope.senderEphPub failed P-256 import",
      cause,
    );
  }

  // Step 3: ECDH and HKDF-derive the wrap key.
  let sharedSecret: Uint8Array;
  try {
    sharedSecret = new Uint8Array(
      await subtle.deriveBits(
        { name: "ECDH", public: senderEphPub },
        ecdhPrivate,
        ECDH_SHARED_SECRET_BYTES * 8,
      ),
    );
  } catch (cause) {
    throw new E2eError(
      "decrypt_failure",
      "ECDH derivation failed against envelope.senderEphPub",
      cause,
    );
  }

  let wrapKey: CryptoKey;
  try {
    wrapKey = await deriveWrapKey(
      sharedSecret,
      envelope.senderEphPub,
      slot.accountIdHash,
    );
  } finally {
    wipe(sharedSecret);
  }

  // Step 4: unwrap MK_msg.
  let mkRaw: Uint8Array;
  try {
    mkRaw = new Uint8Array(
      await subtle.decrypt(
        { name: "AES-GCM", iv: slot.iv },
        wrapKey,
        slot.wrappedKey,
      ),
    );
  } catch (cause) {
    throw new E2eError(
      "decrypt_failure",
      "AES-GCM tag mismatch unwrapping per-recipient key",
      cause,
    );
  }
  if (mkRaw.length !== MK_BYTES) {
    // A wrap whose plaintext is the wrong length means a malformed
    // envelope — Web Crypto already authenticated the bytes, so this
    // is a programmer error somewhere upstream. Surface it as a
    // decrypt failure either way (the caller's UX is the same).
    wipe(mkRaw);
    throw new E2eError(
      "decrypt_failure",
      `unwrapped MK_msg length was ${mkRaw.length}, expected ${MK_BYTES}`,
    );
  }

  // Step 5: decrypt the content.
  let mkKey: CryptoKey;
  try {
    mkKey = await subtle.importKey(
      "raw",
      mkRaw,
      { name: "AES-GCM", length: 256 },
      false,
      ["decrypt"],
    );
  } finally {
    wipe(mkRaw);
  }

  let plaintext: Uint8Array;
  try {
    plaintext = new Uint8Array(
      await subtle.decrypt(
        { name: "AES-GCM", iv: envelope.iv, additionalData: envelope.aad },
        mkKey,
        envelope.ct,
      ),
    );
  } catch (cause) {
    throw new E2eError(
      "decrypt_failure",
      "AES-GCM tag mismatch decrypting content",
      cause,
    );
  }

  // We expect content ciphertext to be at least one tag long when the
  // plaintext is empty. Web Crypto's `decrypt` strips the tag itself,
  // so any ciphertext shorter than `GCM_TAG_BYTES` would have already
  // failed above. Defensive sanity assertion:
  if (envelope.ct.length < GCM_TAG_BYTES) {
    wipe(plaintext);
    throw new E2eError(
      "decrypt_failure",
      `envelope.ct length ${envelope.ct.length} is too short to contain an AES-GCM tag`,
    );
  }

  return plaintext;
}

// ---------------------------------------------------------------------------
// Local helpers
// ---------------------------------------------------------------------------

/**
 * Return a JWK with `alg` and `key_ops` cleared. Web Crypto refuses
 * to import an ECDSA-tagged JWK as ECDH (the `alg` and `key_ops`
 * fields contradict the requested usage); stripping them keeps the
 * scalar / curve-point fields we actually need.
 */
function stripJwkAlg(jwk: JsonWebKey): JsonWebKey {
  const clean: JsonWebKey = {
    kty: jwk.kty,
    crv: jwk.crv,
    x: jwk.x,
    y: jwk.y,
    d: jwk.d,
    ext: jwk.ext,
  };
  return clean;
}
